/// <reference types="../../../typings" />

import { usePathname, useRouter } from 'next/navigation';

import { useMemo } from 'react';

import Cookies from 'js-cookie';

import { sendGoogleOAuthCode } from '@api';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';
import { useTagManager } from '@shared/common/providers/GoogleTagManagerProvider';
import { GOOGLE_AUTH } from '@shared/config/auth';
import { storageKeys } from '@shared/constants';
import { nextProjectUrl } from '@shared/constants/urls';
import displayErrorToast from '@shared/helpers/displayErrorToast';

import { useFeatureTogglesQuery } from '@query/featureToggles';

const useGoogleOAuthClient = () => {
  const { data: featureToggles = {} } = useFeatureTogglesQuery();
  const { refreshSession } = useAuth();
  const { close: closeLoginModal } = useAuthModal();
  const router = useRouter();
  const pathname = usePathname();
  const { triggerAuthEvent } = useTagManager();

  return useMemo(() => {
    if (!featureToggles['oauth2.0']) {
      return null;
    }

    if (typeof window === 'undefined' || !window.google) {
      console.warn('GoogleAuth: google interface is not provided');
      return null;
    }

    return window.google?.accounts?.oauth2?.initCodeClient({
      client_id: GOOGLE_AUTH.clientId,
      scope: GOOGLE_AUTH.scope,
      ux_mode: 'popup',
      callback: async ({ code }: { code: string }) => {
        try {
          const referredBy = Cookies.get(storageKeys.REFERRAL);
          const response = await sendGoogleOAuthCode({ json: { code, referredBy } });
          const isAuth = response?.message === 'Authorized';

          if (!isAuth) {
            throw new Error('Unable to authorize. Try again later.');
          }

          Cookies.remove(storageKeys.REFERRAL);

          const { data: session } = await refreshSession();

          closeLoginModal();

          Cookies.remove(storageKeys.LOGGED_INTO_MAGIC_BOOST);

          if (session?.user) {
            triggerAuthEvent(session.user.id, {
              isNewUser: response.newUser,
            });
          }

          const shouldRedirect = !['/stories/youhodler-100000-yhdl-campaign'].includes(pathname);

          if (!session?.user?.attributes?.name && shouldRedirect) {
            router.push(
              nextProjectUrl.store(
                `/profile/settings?fillProfile=true&from=${window.location.href}&utm_register_method=googleOAuth`,
              ),
            );
          }
        } catch (error) {
          displayErrorToast({ error });
        }
      },
    });
  }, [featureToggles, refreshSession, triggerAuthEvent, closeLoginModal, router, pathname]);
};

export default useGoogleOAuthClient;
